import React from 'react';
import { swatch } from './MoreMenu';
import './TodoClientsDeps.css';

/*
 * THE CLIENT A TO-DO IS FOR (migration 072).
 *
 * Gordon: most of the firm's to-dos are for somebody - a client company -
 * and "everything open for Alpha" should be one click. The companies are the
 * ones the administrators keep in Settings -> Assets -> Companies (062); a
 * to-do names one, and a list may name one as the default for what is added
 * to it.
 *
 * Any member of staff may choose one; nobody may make one here - a new client
 * is an administrator's job, so the picker says where to ask. An archived
 * company stays on what already has it ("Gamma (archived)") and is never
 * offered for anything new. A contractor sees the client on what they can
 * already see and is never shown the picker.
 */

/** The live companies, plus the one already chosen if it has been archived since. */
export const pickable = (companies, current = null) => {
  const live = (companies || []).filter((c) => !c.archived);
  if (current && !live.some((c) => c.id === current.id)) return [...live, { ...current, archived: true }];
  return live;
};

/**
 * "Client" as a field: a select of the live companies, "No client" first.
 * `value` is a company id or ''. `current` is the company the thing already
 * has (an object), so an archived one can still be shown as chosen.
 */
export function ClientPicker({ companies, value, current = null, onChange, disabled = false, label = 'Client',
                               isAdmin = false, hint = true, name = 'company_id' }) {
  const options = pickable(companies, current && current.id === value ? current : null);
  const none = !(companies || []).some((c) => !c.archived);
  return (
    <div className="ctd-field tdc-pick">
      <label>
        <span>{label}</span>
        <select name={name} aria-label={label} value={value || ''} disabled={disabled || companies === null}
                onChange={(e) => onChange(e.target.value || null)}>
          <option value="">{companies === null ? 'Loading…' : 'No client'}</option>
          {options.map((c) => (
            <option key={c.id} value={c.id} disabled={c.archived && c.id !== value}>
              {c.name}{c.short_code ? ` (${c.short_code})` : ''}{c.archived ? ' - archived' : ''}
            </option>
          ))}
        </select>
      </label>
      {hint && (none || value === '' || !value) && (
        <span className="tdc-hint">
          {isAdmin
            ? 'Clients are the companies in Settings → Assets → Companies.'
            : 'Not there? Ask an admin to add it in Settings → Assets → Companies.'}
        </span>
      )}
    </div>
  );
}

/**
 * The client on a row: its short code (or name) on its colour, quiet, like a
 * label. A button when `onClick` is given - "everything open for this
 * client" - and plain text otherwise.
 */
export function ClientChip({ company, onClick = null, full = false }) {
  if (!company) return null;
  const text = !full && company.short_code ? company.short_code : company.name;
  const title = `${company.name}${company.archived ? ' (archived)' : ''}`;
  const cls = `tdc-chip ${swatch(company.color)}${company.archived ? ' is-archived' : ''}`;
  const inner = (
    <>
      <svg className="td-ic tdc-chip-ic" viewBox="0 0 16 16" width="12" height="12" fill="none" stroke="currentColor"
           strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
        <path d="M2.5 13.75V3.5a1 1 0 0 1 1-1h5a1 1 0 0 1 1 1v10.25M9.5 6.5h3a1 1 0 0 1 1 1v6.25M1.5 13.75h13M5 5h2M5 7.5h2M5 10h2M11 9h.5M11 11.25h.5" />
      </svg>
      <span className="tdc-chip-text">{text}</span>
    </>
  );
  if (!onClick) return <span className={cls} data-client-chip={company.id} title={title}>{inner}</span>;
  return (
    <button type="button" className={cls} data-client-chip={company.id} title={`Everything open for ${title}`}
            aria-label={`Client ${company.name}: show everything open for it`}
            onClick={(e) => { e.stopPropagation(); onClick(company); }}>
      {inner}
    </button>
  );
}
